import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { writeData } from "../api";
import { insertPageBlock } from "../api/page";
import { getAllProtocols, getProtocol, searchProtocols } from "../api/protocol";
import { ActionType, AppDispatch, RootState } from "../reducer";
import { AddProtocolsAction } from "../reducer/protocol";
import { newProtocolBlock } from "../types/notebook/note/block/protocol";
import { loadKey, loadStatus } from "./loadStatus";
import { requireRecord, useTrackedLoad } from "./loadTracking";
import { useLoadError } from "../slices/ui";
import {
  Protocol,
  UseProtocolSearchProps,
  newProtocol,
} from "../types/protocol";
import { useAppIdMap } from "./app";
import { useInsertBlock } from "./page/block";
import { useSelectedNotebookPageIdMap } from "./page";

export const useAddProtocols = () => {
  const dispatch = useDispatch<AppDispatch>();
  return (protocols: Protocol[]) => {
    const action: AddProtocolsAction = {
      type: ActionType.ADD_PROTOCOLS,
      protocols,
    };
    dispatch(action as any);
  };
};

export const useInsertProtocolBlock = () => {
  const { selectedNotebookPage } = useSelectedNotebookPageIdMap();
  const insertBlock = useInsertBlock();
  const addProtocols = useAddProtocols();

  return (config: { blockIndex: number }) => {
    const { blockIndex } = config;
    const proto = newProtocol({
      name: "Protocol",
      page: selectedNotebookPage!,
    });
    const block = newProtocolBlock({ proto, blockIndex });
    addProtocols([proto]);
    insertBlock({ block, blockIndex });
    insertPageBlock({ block, "block-index": blockIndex });
    writeData(proto).then((r) => {
      // console.log("AND_WE_WROTE_THEM", r);
    });
    // // console.log("INSERTING_COMPONNNET", blockIndex, comp, block);
    // Promise.all([writeData(comp), writeData(block)]).then((r) => {});
  };
};

export const useProtocol = (protoId: string | undefined) => {
  const protocol = useSelector((state: RootState) => {
    const protos = state.ui.protocols;
    if (!protoId) return undefined;
    if (!(protoId in protos)) return undefined;
    return protos[protoId];
  });
  const addProtocols = useAddProtocols();
  const writeBackend = useMemo(
    () =>
      debounce((protocol: Protocol) => {
        writeData(protocol);
      }, 1000),
    [protoId],
  );
  const setProtocol = (protocol: Protocol) => {
    addProtocols([protocol]);
    writeBackend(protocol);
  };
  const setProtocolName = (name: string) => {
    if (!protocol) return;
    const newProtocol: Protocol = { ...protocol, name };
    setProtocol(newProtocol);
  };
  const setProtocolSpec = (spec: string) => {
    if (!protocol) return;
    const newProtocol: Protocol = { ...protocol, spec };
    setProtocol(newProtocol);
  };

  return { protocol, setProtocol, setProtocolName, setProtocolSpec };
};

export const useFetchProtocol = () => {
  const addProtocols = useAddProtocols();
  const { appId } = useAppIdMap();

  return (protoId: string) => {
    return getProtocol({ "protocol-id": protoId, "app-id": appId! }).then(
      (proto) => {
        addProtocols([proto]);
        return proto;
      },
    );
  };
};

export const useProtocolLoad = (protoId: string | undefined) => {
  const fetchProtocol = useFetchProtocol();
  const trackLoad = useTrackedLoad();
  const { protocol } = useProtocol(protoId);
  const key = protoId ? loadKey("protocol", protoId) : undefined;
  const error = useLoadError(key);
  const retry = () => (protoId && key ? trackLoad(key, requireRecord(fetchProtocol(protoId), "Protocol")) : Promise.resolve(undefined));
  useEffect(() => {
    if (!protoId) return;
    retry();
  }, [protoId]);
  return { protocol, status: loadStatus(protocol, error), retry };
};

export const useFetchProtocolListener = (protoId: string | undefined) => {
  const fetchProtocol = useFetchProtocol();
  useEffect(() => {
    if (!protoId) return;
    fetchProtocol(protoId);
  }, [protoId]);
};

export const useFetchAllProtocols = () => {
  const addProtocols = useAddProtocols();
  const { appId } = useAppIdMap();

  return () => {
    return getAllProtocols({ "app-id": appId! }).then((protos) => {
      addProtocols(protos);
      return protos;
    });
  };
};

export const useFetchAllProtocolsListener = () => {
  const fetchAllProtocols = useFetchAllProtocols();

  useEffect(() => {
    fetchAllProtocols();
  }, []);
};

export const useProtocols = () => {
  const protoMap = useSelector((state: RootState) => state.ui.protocols);
  const protos = Object.values(protoMap);
  return { protocols: protos };
};

export const useProtocolSearch = (
  config: { disabledSearch?: boolean } = { disabledSearch: false },
) => {
  const { disabledSearch } = config;
  const [searchString, setSearchString] = useState<string>("");
  const { appId } = useAppIdMap();
  const [page, setPage] = useState<Protocol[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSearch = useMemo(
    () =>
      debounce((searchString: string) => {
        setIsLoading(true);
        searchProtocols({ "app-id": appId!, search: searchString }).then(
          (protos) => {
            setPage(protos);
            setIsLoading(false);
          },
        );
      }, 200),
    [appId],
  );

  useEffect(() => {
    if (!disabledSearch) {
      handleSearch(searchString);
    }
  }, [searchString, disabledSearch]);

  const useComponentProps: UseProtocolSearchProps = {
    searchString,
    setSearchString,
    page,
    isLoading,
    setIsLoading,
  };

  return useComponentProps;
};
